import React, { FC, useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";
import { objectKeys, objectValues } from "@nens/shared-utils";

import SearchBar from "../components/SearchBar";
import {
  LayerType,
  OneDLayerConfig,
  PIPE_TYPES,
  PipeType,
  getLayers,
  setPipesVisibility,
  setVisibility,
} from "../state/layerOneDMenu";
import { getActiveTool } from "../state/mainGui";
import { setViewLocationAndZoom } from "../state/map";
import { selectAsset } from "../state/selectedAsset";
import { getGeoJson } from "../state/simulation";
import { Asset, displayName } from "../types/assets";
import { geometryToLatLng } from "../utils/geoUtils";

import styles from "./SearchBox.module.css";

const MAX_RESULTS = 15;

function capitalize(s: string) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function removeLastS(s: string) {
  if (s && s.charAt(s.length - 1) === "s") {
    if (s.toLowerCase() === "breaches") {
      return s.slice(0, s.length - 2);
    }
    return s.slice(0, s.length - 1);
  }
  return s;
}

function splitSearchString(searchString: string): string[] {
  // Split on whitespace; remove empty parts; make them lower case.
  return searchString
    .split(/\s+/)
    .filter((part) => part)
    .map((part) => part.toLowerCase());
}

function assetMatchesSearchStrings(asset: Asset, searchStrings: string[]) {
  // Assets get a 'searchString' property in state/appState/fixGeojson()
  // An asset matches the searchStrings array if
  // - That array has at least one member
  // - *Each* element in it is contained in the searchString property
  // Note that parts in the searchString property are separated with spaces, and
  // elements of searchStrings cannot contain whitespace; so a search string match never
  // accidentally spans multiple elements, like the asset type and the display name.
  return (
    searchStrings.length > 0 &&
    searchStrings.every((s) => asset.properties.searchString.includes(s))
  );
}

interface SearchResultProps {
  asset: Asset;
  setViewLocationAndZoom: (location: XYLocation | null, zoom: number) => void;
  selectAsset: (id: string) => void;
  oneDLayerConfig: OneDLayerConfig;
  inputFieldRef: React.RefObject<HTMLInputElement | null>;
}

const SearchResult: FC<SearchResultProps> = ({
  asset,
  setViewLocationAndZoom,
  selectAsset,
  oneDLayerConfig,
  inputFieldRef,
}) => {
  const onClick = useCallback(() => {
    selectAsset(asset.properties!.site_id);
    const latLng = geometryToLatLng(asset);
    if (!latLng) return;
    setViewLocationAndZoom([latLng.lng, latLng.lat], 16);
    if (inputFieldRef.current) {
      inputFieldRef.current.focus();
    }
  }, [asset, setViewLocationAndZoom, selectAsset, oneDLayerConfig, inputFieldRef]);

  const properties = asset.properties!;

  // @ts-ignore
  const description = `${removeLastS(capitalize(properties.model_type))} (${
    properties.model_type === "breaches"
      ? "content_pk " + properties.content_pk
      : "id " + properties.id
  }): ${displayName(asset)}`;

  return (
    <div onClick={onClick} style={{ cursor: "pointer" }}>
      {description}
    </div>
  );
};

interface LayerIsOffProps {
  layer: LayerType | PipeType;
  numResults: number;
  setVisibility: typeof setVisibility;
  setPipesVisibility: typeof setPipesVisibility;
}

const LayerIsOff: FC<LayerIsOffProps> = ({
  layer,
  numResults,
  setVisibility,
  setPipesVisibility,
}) => {
  const onClick = useCallback(() => {
    if (layer in objectValues(PIPE_TYPES)) {
      setPipesVisibility(layer as PipeType, true);
    } else {
      setVisibility(layer as LayerType, true);
    }
  }, [setVisibility, setPipesVisibility, layer]);

  return (
    <div key={layer} className={styles.TooManyResults}>
      {numResults} results of type {layer},&nbsp;
      <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={onClick}>
        click to make visible
      </span>
    </div>
  );
};

interface SearchboxProps {
  setViewLocationAndZoom: typeof setViewLocationAndZoom;
  selectAsset: (id: string) => void;
  setVisibility: typeof setVisibility;
  setPipesVisibility: typeof setPipesVisibility;
}

const SearchBox: React.FC<SearchboxProps> = ({
  setViewLocationAndZoom,
  selectAsset,
  setVisibility,
  setPipesVisibility,
}) => {
  const geojson = useSelector(getGeoJson);
  const layersOneDMenu = useSelector(getLayers);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const [searchString, setSearchString] = useState<string>("");
  const activeTool = useSelector(getActiveTool);
  const intl = useIntl();

  const invisibleLayers: { [key: string]: number } = {};
  objectKeys(layersOneDMenu).forEach((layer) => {
    if (layer !== "pipes" && !layersOneDMenu[layer].visible) {
      invisibleLayers[layer] = 0;
    } else if (layer === "pipes") {
      Object.keys(PIPE_TYPES).forEach((key) => {
        const pipeType = PIPE_TYPES[key];
        if (
          layersOneDMenu.pipes.pipeStyles[pipeType] &&
          layersOneDMenu.pipes.pipeStyles[pipeType].visible === false
        ) {
          invisibleLayers[pipeType] = 0;
        }
      });
    }
  });

  const assets = (geojson ? geojson.features : []) as Asset[];

  const searchStrings = splitSearchString(searchString);
  // Actual search happens here
  const allResults = assets.filter((asset) => assetMatchesSearchStrings(asset, searchStrings));

  allResults.forEach((result) => {
    if (invisibleLayers[result.properties.model_type] !== undefined) {
      invisibleLayers[result.properties.model_type]++;
    } else if (result.properties.model_type === "pipes") {
      Object.keys(PIPE_TYPES).forEach((key) => {
        if (
          result.properties.model_type === "pipes" &&
          result.properties.sewerage_type + "" === key
        ) {
          invisibleLayers[PIPE_TYPES[key]]++;
        }
      });
    }
  });

  const results = allResults.filter((feature) => {
    if (invisibleLayers[feature.properties.model_type]) {
      return false;
    } else if (
      feature.properties.model_type === "pipes" &&
      invisibleLayers[PIPE_TYPES[feature.properties.sewerage_type]]
    ) {
      return false;
    } else {
      return true;
    }
  });

  const onEnter = useCallback(() => {
    // If result length is exactly 1, pressing enter is the same as clicking it
    if (results.length === 1) {
      selectAsset(results[0].properties!.site_id);
      const latLng = geometryToLatLng(results[0]);
      if (!latLng) return;
      setViewLocationAndZoom([latLng.lng, latLng?.lat], 16);
    }
  }, [results, layersOneDMenu, selectAsset, setViewLocationAndZoom]);

  return (
    <div
      className={`${styles.SearchBox} ${
        activeTool !== null ? styles.SearchBoxLeftSideMenuOpen : null
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <SearchBar
        searchString={searchString}
        setSearchString={setSearchString}
        autoFocus={false}
        setPage={() => null}
        placeholder={intl.formatMessage({
          id: "search.placeholder",
          defaultMessage: "Search for asset type, name, code or id",
        })}
        inputFieldRef={inputFieldRef}
        onEnter={onEnter}
      />
      {searchString && searchStrings.length > 0 ? (
        <div className={styles.SearchResults} onClick={(e) => e.stopPropagation()}>
          {results.slice(0, MAX_RESULTS).map((ob) => (
            <SearchResult
              key={ob.properties!.site_id}
              asset={ob}
              setViewLocationAndZoom={setViewLocationAndZoom}
              selectAsset={selectAsset}
              oneDLayerConfig={layersOneDMenu[ob.properties.model_type]}
              inputFieldRef={inputFieldRef}
            />
          ))}
          {results.length > MAX_RESULTS ? (
            <div className={styles.TooManyResults} key="more">
              {results.length - MAX_RESULTS} more results, please refine your search.
            </div>
          ) : null}
          {(Object.keys(invisibleLayers) as (LayerType | PipeType)[]).map((layer) =>
            invisibleLayers[layer] ? (
              <LayerIsOff
                key={layer}
                layer={layer}
                numResults={invisibleLayers[layer]}
                setVisibility={setVisibility}
                setPipesVisibility={setPipesVisibility}
              />
            ) : null
          )}
        </div>
      ) : null}
    </div>
  );
};

export default connect(null, {
  setViewLocationAndZoom,
  selectAsset,
  setVisibility,
  setPipesVisibility,
})(SearchBox);
